import React from "react";
import styles from "./STContactUs.module.css";
import clsx from "clsx";
import { STHeroCommon } from "../STHeroCommon";
import Link from "next/link";
import { Icons } from "@/const/icons";
import { STSelect } from "../STSelect";
import { STInput } from "../STInput";
import { STTextarea } from "../STTextarea";
import { STButton } from "../STButton";
import { contactUsPoster } from "@/assets/images";
import Image from "next/image";
import { useContactUs } from "@/hooks";
import { getPageContentBySlug } from "@/actions";

export const STContactUs = ({ pageData }) => {
  const [data, setData] = React.useState(pageData);

  React.useEffect(() => {
    if (!pageData) {
      getPageContentBySlug("contact-us")
        .then((res) => {
          if (res?.success && res?.data) {
            setData(res.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching contact-us page data client-side:", err);
        });
    } else {
      setData(pageData);
    }
  }, [pageData]);

  const pageContent = data?.page?.page_content || {};
  const imageUrlBase = data?.imageUrl || "";

  const email = pageContent.email || "findsolution@sustanix.com";
  const phone = pageContent.phone || "+31 6 43 54 78 65";
  const address =
    pageContent.address || "Urmonderbaan 22, 6167RD, Geleen, Netherlands";
  const buttonText =
    pageContent.button_text || pageContent.buttonText || "Get direction";
  const mapLink =
    pageContent.map_link ||
    pageContent.location_url ||
    "https://maps.app.goo.gl/7diubmi1UeKs6hDEA";

  const bannerBgUrl =
    pageContent.banner_background_url ||
    pageContent.banner_image_url ||
    pageContent.cover_photo_url;

  const rawCover =
    pageContent.banner_background ||
    pageContent.bannerBackground ||
    pageContent.banner_image ||
    pageContent.cover_photo ||
    pageContent.coverPhoto ||
    pageContent.image;

  let bannerImg = contactUsPoster;
  if (bannerBgUrl) {
    bannerImg = bannerBgUrl;
  } else if (rawCover) {
    if (
      typeof rawCover === "string" &&
      (rawCover.startsWith("http://") ||
        rawCover.startsWith("https://") ||
        rawCover.startsWith("/"))
    ) {
      bannerImg = rawCover;
    } else if (imageUrlBase) {
      bannerImg = imageUrlBase.endsWith("/")
        ? `${imageUrlBase}${rawCover}`
        : `${imageUrlBase}/${rawCover}`;
    } else {
      bannerImg = rawCover;
    }
  }

  const {
    formData,
    loading,
    errors,
    success,
    handleChange,
    handleSelectChange,
    handleSubmit,
  } = useContactUs();

  return (
    <>
      <STHeroCommon
        title={pageContent.title || "Contact us"}
        description={
          pageContent.description ||
          "For inquiries, collaborations, or technical discussions, please reach out to us through the details below"
        }
      />
      <section className="space-xl-py">
        <div className="container">
          <div
            className={clsx(
              styles.letsTalkWrapper,
              "d-flex flex-column flex-xl-row gap-lg",
            )}
          >
            <div
              className={clsx(styles.letsTalkContent, "w-100 text-clr-white")}
            >
              <h2 className={clsx(styles.letsTalkTitle, "primary-title")}>
                Let's talk
              </h2>

              <div className="d-flex flex-column gap-lg align-items-start space-md-mt">
                {success && (
                  <p className="text-success mb-0">
                    Message sent successfully!
                  </p>
                )}
                <div
                  className={clsx(
                    styles.letsTalkFormTopRow,
                    "d-grid gap-md w-100",
                  )}
                >
                  <STSelect
                    placeholder="I am enquiring as a"
                    options={[
                      {
                        label: "Industry associate",
                        value: "Industry associate",
                      },
                      { label: "Investor", value: "Investor" },
                      { label: "Individual", value: "Individual" },
                    ]}
                    variant="light"
                    value={formData.type}
                    onChange={handleSelectChange}
                    error={errors.type}
                  />
                  <STInput
                    placeholder="Name"
                    variant="light"
                    className={"w-100"}
                    name="name"
                    value={formData.name}
                    handleChange={handleChange}
                    error={errors.name}
                  />

                  <STInput
                    placeholder="Email address"
                    variant="light"
                    className={"w-100"}
                    name="email"
                    value={formData.email}
                    handleChange={handleChange}
                    error={errors.email}
                  />
                </div>
                <div
                  className={clsx(
                    styles.bottomRow,
                    "d-flex gap-md w-100 align-items-start align-items-sm-center flex-column flex-sm-row",
                  )}
                >
                  <STInput
                    placeholder="Share your thoughts with us..."
                    variant="light"
                    className="w-100"
                    name="message"
                    value={formData.message}
                    handleChange={handleChange}
                    error={errors.message}
                  />

                  <STButton
                    variant="black"
                    suffix={Icons.arrowRight}
                    className={clsx(styles.getInTouchBtn, "flex-shrink-0")}
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    Connect
                  </STButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="space-xl-pb">
        <div className="container">
          <div className={clsx("position-relative")}>
            <div
              className={clsx(
                styles.mediaWrapper,
                "ratio radius-xl bg-off-white overflow-hidden",
              )}
            >
              <Image
                src={bannerImg}
                alt={pageContent.title || "Contact Us Banner"}
                fill
                className={clsx("object-fit-cover")}
                unoptimized={typeof bannerImg === "string" && bannerImg.startsWith("http")}
              />
            </div>

            <div
              className={clsx(
                styles.pillsWrapper,
                "position-absolute start-0 d-flex gap-xs align-items-center justify-content-center justify-content-md-between flex-wrap gap-xs w-100 z-2",
              )}
            >
              <div className={clsx("d-flex gap-xs flex-wrap")}>
                <Link
                  href={`mailto:${email}`}
                  className={clsx(
                    styles.contactPill,
                    "bg-clr-white gap-2 text-black p fw-medium d-flex align-items-center",
                  )}
                >
                  <span className={styles.pillIcon}>{Icons.emailIcon}</span>
                  <span className="d-none d-md-block">
                    {email}
                  </span>
                </Link>

                <Link
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className={clsx(
                    styles.contactPill,
                    "bg-clr-white gap-2 text-black p fw-medium d-flex align-items-center",
                  )}
                >
                  <span className={styles.pillIcon}>{Icons.phoneIcon}</span>
                  <span className="d-none d-md-block"> {phone}</span>
                </Link>
              </div>

              <div className={clsx("d-flex gap-xs flex-wrap")}>
                <div
                  className={clsx(
                    styles.contactPill,
                    "bg-clr-white gap-2 text-black p fw-medium d-flex align-items-center",
                  )}
                >
                  <span className={clsx(styles.pillIcon, "text-clr-desire")}>
                    {Icons.pinIcon}
                  </span>
                  <span className="d-none d-md-block">
                    {address}
                  </span>
                </div>

                <Link
                  href={mapLink}
                  target="_blank"
                  className={clsx(
                    styles.contactPill,
                    "bg-clr-white gap-2 text-black p fw-medium d-flex align-items-center",
                  )}
                >
                  <span className={clsx(styles.pillIcon, "text-clr-blue")}>
                    {Icons.navigationArrow}
                  </span>
                  <span className="d-none d-md-block"> {buttonText}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
